/**
 * マッチング設定一覧テーブル
 */
import type { FC } from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import type { MatchingSetting } from "../../../hooks/use-matching-settings-api";

type MatchingSettingsTableProps = {
  settings: MatchingSetting[];
  onActivate: (settingId: string) => Promise<void>;
  onDeactivate: (settingId: string) => Promise<void>;
  onDelete: (settingId: string) => Promise<void>;
  isActivating: boolean;
  isDeleting: boolean;
};

const matchingModeLabels: Record<string, string> = {
  manual: "手動",
  review: "レビュー",
  auto: "自動",
};

export const MatchingSettingsTable: FC<MatchingSettingsTableProps> = ({
  settings,
  onActivate,
  onDeactivate,
  onDelete,
  isActivating,
  isDeleting,
}) => {
  const [actionId, setActionId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const handleActivate = async (settingId: string) => {
    setActionId(settingId);
    try {
      await onActivate(settingId);
    } finally {
      setActionId(null);
    }
  };

  const handleDeactivate = async (settingId: string) => {
    setActionId(settingId);
    try {
      await onDeactivate(settingId);
    } finally {
      setActionId(null);
    }
  };

  const handleDelete = async (settingId: string) => {
    setActionId(settingId);
    try {
      await onDelete(settingId);
    } finally {
      setActionId(null);
      setDeleteConfirm(null);
    }
  };

  if (settings.length === 0) {
    return (
      <div className="card bg-base-100 shadow-sm">
        <div className="card-body">
          <p className="text-base-content/60">
            マッチング設定がまだありません。「新規作成」から設定を追加してください。
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="card bg-base-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table table-zebra">
            <thead>
              <tr>
                <th>設定名</th>
                <th>ステータス</th>
                <th>マッチングモード</th>
                <th>最低マージン</th>
                <th>最低スコア</th>
                <th>メール有効期間</th>
                <th>更新日時</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {settings.map(setting => (
                <tr key={setting.id}>
                  <td>
                    <div>
                      <div className="font-medium">{setting.name}</div>
                      {setting.description && (
                        <div className="text-sm text-base-content/60 truncate max-w-xs">
                          {setting.description}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    {setting.isActive ? (
                      <span className="badge badge-success badge-sm">
                        アクティブ
                      </span>
                    ) : (
                      <span className="badge badge-ghost badge-sm">
                        非アクティブ
                      </span>
                    )}
                  </td>
                  <td>
                    {matchingModeLabels[setting.matchingMode] ||
                      setting.matchingMode}
                  </td>
                  <td>{setting.minMargin.toLocaleString()}円</td>
                  <td>{Math.round(setting.minTotalScore * 100)}%</td>
                  <td>
                    {setting.maxEmailAgeDays
                      ? `${setting.maxEmailAgeDays}日`
                      : "制限なし"}
                  </td>
                  <td className="text-sm">
                    {new Date(setting.updatedAt).toLocaleDateString("ja-JP")}
                  </td>
                  <td>
                    <div className="flex gap-1">
                      {setting.isActive ? (
                        <button
                          type="button"
                          className="btn btn-ghost btn-xs"
                          onClick={() => handleDeactivate(setting.id)}
                          disabled={isActivating && actionId === setting.id}
                        >
                          {isActivating && actionId === setting.id ? (
                            <span className="loading loading-spinner loading-xs" />
                          ) : (
                            "無効化"
                          )}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-ghost btn-xs"
                          onClick={() => handleActivate(setting.id)}
                          disabled={isActivating && actionId === setting.id}
                        >
                          {isActivating && actionId === setting.id ? (
                            <span className="loading loading-spinner loading-xs" />
                          ) : (
                            "有効化"
                          )}
                        </button>
                      )}
                      <Link
                        to={`/settings/matching/conditions/${setting.id}/edit`}
                        className="btn btn-ghost btn-xs"
                      >
                        編集
                      </Link>
                      {!setting.isActive && (
                        <button
                          type="button"
                          className="btn btn-ghost btn-xs text-error"
                          onClick={() => setDeleteConfirm(setting.id)}
                          disabled={isDeleting && actionId === setting.id}
                        >
                          削除
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {deleteConfirm && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">設定を削除しますか？</h3>
            <p className="py-4">この操作は取り消せません。</p>
            <div className="modal-action">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setDeleteConfirm(null)}
                disabled={isDeleting}
              >
                キャンセル
              </button>
              <button
                type="button"
                className="btn btn-error"
                onClick={() => handleDelete(deleteConfirm)}
                disabled={isDeleting}
              >
                {isDeleting && actionId === deleteConfirm ? (
                  <span className="loading loading-spinner loading-sm" />
                ) : (
                  "削除"
                )}
              </button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => !isDeleting && setDeleteConfirm(null)}
          />
        </div>
      )}
    </>
  );
};
